import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDownLeft,
  ArrowUpRight,
  History,
  PackageX,
  Scale,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { ProductSummary, StockBalanceSummary } from '@aeris/contracts'
import { ZERO_QUANTITY, formatQuantity, parseQuantity } from '@aeris/inventory'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import {
  ApiError,
  fetchBranches,
  fetchStockBalances,
  releaseProductStockHold,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatDate } from '../lib/date-format.ts'

import { StockHistoryDialog } from './stock-history-dialog.tsx'
import {
  StockMovementForm,
  type StockOperation,
} from './stock-movement-form.tsx'

/**
 * O estoque de um produto, dentro da ficha — F131.
 *
 * *"No editar produto tenho que conseguir já editar o estoque também, alterar,
 * ver histórico."* Continua sem campo de quantidade (seção 12: saldo é da
 * filial, num livro-razão): o que a ficha ganha são as MESMAS três ações da
 * tela de Estoque — contar, dar entrada, dar saída — e o histórico, sem sair do
 * produto. Cada uma grava um movimento com o nome de quem fez, pelas mesmas
 * rotas e com as mesmas permissões; a ficha não é uma segunda porta.
 */

const asQuantity = (value: string): string =>
  formatQuantity(parseQuantity(value) ?? ZERO_QUANTITY)

export type ProductStockPanelProps = {
  product: ProductSummary
  /** A empresa não conta estoque (F102): mostra o passado e não movimenta. */
  stockDisabledBecause: string | null
  /**
   * Um movimento ou o "Liberar" mudou o produto (F248): a versão que o Editar
   * tem ficou velha, e salvar daria "alterado por outra pessoa".
   */
  onProductChanged?: (() => void) | undefined
}

export const ProductStockPanel = ({
  product,
  stockDisabledBecause,
  onProductChanged,
}: ProductStockPanelProps): ReactNode => {
  const { allows, session } = useAuth()
  const [branchId, setBranchId] = useState(session?.context?.branchId ?? '')
  const [movement, setMovement] = useState<{
    operation: StockOperation
    balance: StockBalanceSummary
  } | null>(null)
  const [historyFor, setHistoryFor] = useState<StockBalanceSummary | null>(null)

  const canWrite = allows('inventory.write') && stockDisabledBecause === null
  const canAdjust = allows('inventory.adjust') && stockDisabledBecause === null
  // Liberar não movimenta estoque, então vale mesmo com a empresa sem contar —
  // é justamente o caso em que ninguém vai dar entrada (F133).
  const canAdjustAny = allows('inventory.adjust')

  const queryClient = useQueryClient()
  const [confirmRelease, setConfirmRelease] = useState(false)
  const [releaseError, setReleaseError] = useState<string | null>(null)
  const release = useMutation({
    mutationFn: () => releaseProductStockHold(product.id, product.version),
    onSuccess: () => {
      setConfirmRelease(false)
      setReleaseError(null)
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      onProductChanged?.()
    },
    onError: (error: unknown) => {
      setConfirmRelease(false)
      setReleaseError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível liberar o produto. Tente de novo.'
      )
    },
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
  })

  const balances = useQuery({
    queryKey: ['stock-balances', { productId: product.id, branchId }],
    queryFn: ({ signal }) =>
      fetchStockBalances(
        {
          productId: product.id,
          ...(branchId === '' ? {} : { branchId }),
          pageSize: 100,
        },
        signal
      ),
  })

  const branchList = branches.data?.branches ?? []
  const items = balances.data?.items ?? []

  return (
    <div className="flex flex-col gap-3">
      {stockDisabledBecause !== null ? (
        <p
          role="status"
          className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-2 text-(length:--text-xs) text-(--text-secondary)"
        >
          {stockDisabledBecause}
        </p>
      ) : !product.tracksStock ? (
        <p
          role="status"
          className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-2 text-(length:--text-xs) text-(--text-secondary)"
        >
          Este produto não controla estoque: as vendas não mexem no saldo. Para
          controlar, marque &quot;Controlar estoque deste produto&quot; na aba
          Geral e salve.
        </p>
      ) : (
        <p className="text-(length:--text-xs) text-(--text-secondary)">
          O saldo muda por movimento, nunca digitado por cima: cada contagem,
          entrada ou saída fica no histórico com o nome de quem fez.
        </p>
      )}

      {product.awaitingStockSince === null ? null : (
        <InlineAlert
          tone="warning"
          title="Aguardando a conferência do estoque"
          action={
            canAdjustAny ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setConfirmRelease(true)
                }}
              >
                Liberar sem contar
              </Button>
            ) : undefined
          }
        >
          Este produto não vende até alguém conferir: faça uma entrada ou um
          ajuste abaixo e ele é liberado sozinho.
        </InlineAlert>
      )}

      {releaseError === null ? null : (
        <InlineAlert tone="danger">{releaseError}</InlineAlert>
      )}

      <ConfirmDialog
        open={confirmRelease}
        onOpenChange={setConfirmRelease}
        title="Liberar sem contar?"
        description="O produto volta a vender agora, sem nenhum movimento de estoque. Fica registrado quem liberou."
        confirmLabel="Liberar"
        cancelLabel="Voltar"
        loading={release.isPending}
        onConfirm={() => {
          release.mutate()
        }}
      />

      {branchList.length > 1 ? (
        <div className="flex items-center gap-2">
          <label
            htmlFor="product-stock-branch"
            className="text-(length:--text-xs) text-(--text-secondary)"
          >
            Filial
          </label>
          <Select
            id="product-stock-branch"
            value={branchId === '' ? (balances.data?.branchId ?? '') : branchId}
            onChange={(event) => {
              setBranchId(event.target.value)
            }}
            className="w-56"
          >
            {branchList.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      {balances.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : balances.isError ? (
        <EmptyState
          icon={<PackageX aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar o estoque"
          description="Verifique a conexão com o servidor e se há uma filial selecionada."
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<PackageX aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma variante ativa"
          description="Um produto arquivado não aparece no estoque."
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Código</TableHeaderCell>
                <TableHeaderCell numeric>Em estoque</TableHeaderCell>
                <TableHeaderCell numeric>Disponível</TableHeaderCell>
                <TableHeaderCell>Último movimento</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">Ações</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((balance) => (
                <TableRow key={balance.variantId}>
                  <TableCell className="font-(--font-mono) whitespace-nowrap">
                    {balance.sku}
                  </TableCell>
                  <TableCell numeric>
                    <strong className="font-(--weight-medium)">
                      {asQuantity(balance.onHand)}
                    </strong>{' '}
                    <span className="text-(--text-muted)">{balance.unit}</span>
                  </TableCell>
                  <TableCell numeric className="text-(--text-secondary)">
                    {asQuantity(balance.available)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-(--text-secondary)">
                    {balance.updatedAt === null
                      ? 'nunca movimentado'
                      : formatDate(balance.updatedAt)}
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-wrap justify-end gap-1">
                      {canAdjust && product.tracksStock ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          leadingIcon={
                            <Scale aria-hidden="true" className="h-4 w-4" />
                          }
                          onClick={() => {
                            setMovement({ operation: 'adjustment', balance })
                          }}
                        >
                          Ajustar
                        </Button>
                      ) : null}
                      {canWrite && product.tracksStock ? (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            leadingIcon={
                              <ArrowDownLeft
                                aria-hidden="true"
                                className="h-4 w-4"
                              />
                            }
                            onClick={() => {
                              setMovement({ operation: 'entry', balance })
                            }}
                          >
                            Entrada
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            leadingIcon={
                              <ArrowUpRight
                                aria-hidden="true"
                                className="h-4 w-4"
                              />
                            }
                            onClick={() => {
                              setMovement({ operation: 'exit', balance })
                            }}
                          >
                            Saída
                          </Button>
                        </>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        leadingIcon={
                          <History aria-hidden="true" className="h-4 w-4" />
                        }
                        onClick={() => {
                          setHistoryFor(balance)
                        }}
                      >
                        Histórico
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {movement === null ? null : (
        <StockMovementForm
          key={`${movement.balance.variantId}-${movement.operation}`}
          operation={movement.operation}
          balance={movement.balance}
          onClose={() => {
            setMovement(null)
          }}
          onRecorded={onProductChanged}
        />
      )}

      {historyFor === null ? null : (
        <StockHistoryDialog
          key={historyFor.variantId}
          balance={historyFor}
          onClose={() => {
            setHistoryFor(null)
          }}
        />
      )}
    </div>
  )
}
